/**
 * EmailEntryStep Component
 * First step of the onboarding flow - email entry and Google SSO.
 */

import React from "react";
import { useForm } from "react-hook-form";
import { Box, TextField, Button, Typography } from "../../design-system";
import { GoogleSignUpButton } from "../../signupLogin/GoogleSignUpButton";
import { useCsrfToken } from "../../../hooks";
import { ensureCsrfCookie } from "../../../api/auth";
import { EMAIL_STEP, ERROR_MESSAGES } from "../constants/onboardingText";
import LegalConsent from "../modal/LegalConsent";

interface EmailEntryStepProps {
  onSubmit: (email: string, csrfToken: string) => Promise<void>;
  onGoogleSuccess: (credentialResponse: { credential: string }) => void;
  onGoogleError: () => void;
  loading?: boolean;
  error?: string | null;
}

interface EmailFormData {
  email: string;
}

const EmailEntryStep: React.FC<EmailEntryStepProps> = ({
  onSubmit,
  onGoogleSuccess,
  onGoogleError,
  loading = false,
  error = null,
}) => {
  const csrfToken = useCsrfToken();
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors },
    clearErrors,
  } = useForm<EmailFormData>();
  const emailValue = watch("email");

  const displayError = errors.email?.message || error;

  const handleFormSubmit = async (data: EmailFormData) => {
    clearErrors("email");

    const trimmedEmail = data.email?.trim() || "";

    if (!trimmedEmail) {
      setError("email", { message: ERROR_MESSAGES.EMAIL_REQUIRED });
      return;
    }

    // Self-heal: a brand-new visitor may not have the CSRF cookie yet (the app-load fetch can
    // race or hiccup). Fetch + set it NOW instead of failing with "CSRF token not found".
    let token = csrfToken;
    if (!token) {
      token = await ensureCsrfCookie();
    }
    if (!token) {
      setError("email", { message: ERROR_MESSAGES.CSRF_NOT_FOUND });
      return;
    }

    await onSubmit(trimmedEmail, token);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        gap: "24px",
      }}
    >
      <Typography variant="headingDisplay" color="white" component="h1">
        {EMAIL_STEP.TITLE}
      </Typography>

      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        noValidate
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          gap: "24px",
        }}
      >
        <GoogleSignUpButton
          onSuccess={onGoogleSuccess}
          onError={onGoogleError}
        />
        <Typography variant="body" color="white" sx={{ textAlign: "center" }}>
          {EMAIL_STEP.OR_DIVIDER}
        </Typography>

        <TextField
          {...register("email")}
          value={emailValue || ""}
          required
          type="email"
          placeholder={EMAIL_STEP.EMAIL_PLACEHOLDER}
          error={!!displayError}
          helperText={displayError || ""}
          fullWidth
          onClear={() => {
            setValue("email", "");
            clearErrors("email");
          }}
        />

        <Button
          type="submit"
          variant="primary"
          loading={loading}
          disabled={loading}
          fullWidth
        >
          {EMAIL_STEP.SUBMIT_BUTTON}
        </Button>
      </form>

      <LegalConsent />
    </Box>
  );
};

export default EmailEntryStep;
